import React, { useState, useEffect } from 'react';
import './SimuladorVista.css';

function SimuladorVista({ 
  tituloGeneral, 
  preguntas, 
  configuracionGradificacion, 
  onCerrar 
}) {
  // Estado del simulador
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestasSeleccionadas, setRespuestasSeleccionadas] = useState({});
  const [puntajeAcumulado, setPuntajeAcumulado] = useState(0);
  const [evaluacionCompleta, setEvaluacionCompleta] = useState(false);
  const [resultadoFinal, setResultadoFinal] = useState(null);
  const [mostrandoTransicion, setMostrandoTransicion] = useState(false);

  // Motor de evaluación
  const procesarRespuesta = (respuesta, indicePregunta) => {
    const nuevasRespuestas = { ...respuestasSeleccionadas, [indicePregunta]: respuesta };
    setRespuestasSeleccionadas(nuevasRespuestas);

    // Determinar acción basada en tipo de gradificación y respuesta
    const accion = determinarAccion(respuesta, configuracionGradificacion.tipoGradificacion);
    
    if (accion.tipo === 'finalizar') {
      finalizarEvaluacion(accion.resultado, nuevasRespuestas);
    } else if (accion.tipo === 'acumular_puntos') {
      const nuevoPuntaje = puntajeAcumulado + (respuesta.puntos || 0);
      setPuntajeAcumulado(nuevoPuntaje);
      
      // Si es la última pregunta, evaluar por puntajes
      if (indicePregunta === preguntas.length - 1) {
        const gradoPorPuntaje = evaluarPorPuntajes(nuevoPuntaje);
        finalizarEvaluacion(gradoPorPuntaje, nuevasRespuestas);
      } else {
        avanzarPregunta();
      }
    } else {
      avanzarPregunta();
    }
  };

  const determinarAccion = (respuesta, tipoGradificacion) => {
    if (tipoGradificacion === 'puntajes') {
      return { tipo: 'acumular_puntos' };
    }
    
    if (tipoGradificacion === 'grados_directos') {
      if (respuesta.accionSiguiente === 'finalizar_con_grado') {
        const grado = respuesta.gradoDirecto === 'sin_valor' ? 
          { id: 'sin_valor', nombre: 'Sin valor', descripcion: 'No ofrecer dinero' } :
          configuracionGradificacion.grados.find(g => g.id === respuesta.gradoDirecto);
        return { tipo: 'finalizar', resultado: grado };
      }
      if (respuesta.accionSiguiente === 'no_retomable') {
        return { tipo: 'finalizar', resultado: { id: 'no_retomable', nombre: 'No Retomable', descripcion: 'Dispositivo no apto para retoma' } };
      }
      return { tipo: 'continuar' };
    }
    
    if (tipoGradificacion === 'hibrida') {
      if (respuesta.accionSiguiente === 'finalizar_con_grado') {
        const grado = respuesta.gradoDirecto === 'sin_valor' ? 
          { id: 'sin_valor', nombre: 'Sin valor', descripcion: 'No ofrecer dinero' } :
          configuracionGradificacion.grados.find(g => g.id === respuesta.gradoDirecto);
        return { tipo: 'finalizar', resultado: grado };
      }
      if (respuesta.accionSiguiente === 'no_retomable') {
        return { tipo: 'finalizar', resultado: { id: 'no_retomable', nombre: 'No Retomable', descripcion: 'Dispositivo no apto para retoma' } };
      }
      if (respuesta.accionSiguiente === 'solo_puntos') {
        return { tipo: 'acumular_puntos' };
      }
    }
    
    return { tipo: 'continuar' };
  };

  const evaluarPorPuntajes = (puntaje) => {
    const threshold = configuracionGradificacion.thresholds.find(t => 
      puntaje >= t.min && puntaje <= t.max
    );
    
    if (threshold) {
      return configuracionGradificacion.grados.find(g => g.id === threshold.gradoId);
    }
    
    // Fallback: último grado disponible
    return configuracionGradificacion.grados[configuracionGradificacion.grados.length - 1];
  };

  const finalizarEvaluacion = (resultado, respuestas) => {
    setMostrandoTransicion(true);
    
    setTimeout(() => {
      setResultadoFinal(resultado);
      setEvaluacionCompleta(true);
      setMostrandoTransicion(false);
    }, 1000);
  };

  const avanzarPregunta = () => {
    setMostrandoTransicion(true);
    
    setTimeout(() => {
      setPreguntaActual(preguntaActual + 1);
      setMostrandoTransicion(false);
    }, 500);
  };

  const reiniciarSimulador = () => {
    setPreguntaActual(0);
    setRespuestasSeleccionadas({});
    setPuntajeAcumulado(0);
    setEvaluacionCompleta(false);
    setResultadoFinal(null);
    setMostrandoTransicion(false);
  };

  const pregunta = preguntas[preguntaActual];

  if (evaluacionCompleta) {
    return (
      <div className="simulador-overlay">
        <div className="simulador-container">
          <div className="phone-mockup">
            <div className="phone-screen">
              <div className="resultado-final">
                <div className="resultado-header">
                  <div className="resultado-icon">
                    {resultadoFinal?.id === 'no_retomable' ? '❌' : 
                     resultadoFinal?.id === 'sin_valor' ? '💸' : '✅'}
                  </div>
                  <h2 className="resultado-titulo">Evaluación Completada</h2>
                </div>
                
                <div className="resultado-contenido">
                  <div className="resultado-grado">
                    <div 
                      className="grado-color"
                      style={{ backgroundColor: resultadoFinal?.color || '#6366F1' }}
                    ></div>
                    <div className="grado-info">
                      <h3>{resultadoFinal?.nombre}</h3>
                      <p>{resultadoFinal?.descripcion}</p>
                    </div>
                  </div>
                  
                  {configuracionGradificacion.tipoGradificacion === 'puntajes' && (
                    <div className="puntaje-final">
                      <span className="puntaje-label">Puntaje Total:</span>
                      <span className="puntaje-valor">{puntajeAcumulado} puntos</span>
                    </div>
                  )}
                  
                  <div className="resumen-respuestas">
                    <h4>Resumen de Respuestas:</h4>
                    {Object.entries(respuestasSeleccionadas).map(([indice, respuesta]) => (
                      <div key={indice} className="respuesta-resumen">
                        <strong>{preguntas[indice].titulo}:</strong> {respuesta.texto}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="resultado-acciones">
                  <button className="btn-simulador btn-outline" onClick={reiniciarSimulador}>
                    🔄 Probar de Nuevo
                  </button>
                  <button className="btn-simulador btn-primary" onClick={onCerrar}>
                    ✅ Cerrar Simulador
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="simulador-controles">
            <button className="btn btn-outline" onClick={onCerrar}>
              ✕ Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!pregunta) {
    return null;
  }

  return (
    <div className="simulador-overlay">
      <div className="simulador-container">
        <div className="phone-mockup">
          <div className="phone-screen">
            {mostrandoTransicion ? (
              <div className="transicion-pantalla">
                <div className="loading-spinner"></div>
                <p>Procesando...</p>
              </div>
            ) : (
              <>
                {/* Header con título */}
                <div className="pregunta-header">
                  <h1 className="titulo-general">{tituloGeneral}</h1>
                  <div className="progreso-pregunta">
                    {preguntaActual + 1} de {preguntas.length}
                  </div>
                </div>

                {/* Carrusel de imágenes (solo si hay imágenes) */}
                {pregunta.imagenes && pregunta.imagenes.length > 0 && (
                  <div className="imagenes-carrusel">
                    <div className="carrusel-contenedor">
                      {pregunta.imagenes.map((imagen, index) => (
                        <img 
                          key={imagen.id} 
                          src={imagen.url} 
                          alt={`Referencia ${index + 1}`}
                          className="imagen-referencia"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Contenido de la pregunta */}
                <div className="pregunta-contenido">
                  <h2 className="pregunta-titulo">{pregunta.titulo}</h2>
                  <p className="pregunta-texto">{pregunta.pregunta}</p>
                  
                  {pregunta.ejemplos && (
                    <div className="pregunta-ejemplos">
                      <p><strong>Ejemplos:</strong></p>
                      <p>{pregunta.ejemplos}</p>
                    </div>
                  )}
                </div>

                {/* Opciones de respuesta */}
                <div className="respuestas-container">
                  {pregunta.respuestas.map((respuesta, index) => (
                    <button
                      key={respuesta.id}
                      className="respuesta-boton"
                      onClick={() => procesarRespuesta(respuesta, preguntaActual)}
                    >
                      <span className="respuesta-numero">{index + 1}</span>
                      <span className="respuesta-texto">{respuesta.texto}</span>
                      {configuracionGradificacion.tipoGradificacion === 'puntajes' && respuesta.puntos > 0 && (
                        <span className="respuesta-puntos">{respuesta.puntos} pts</span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="simulador-controles">
          <button className="btn btn-outline" onClick={onCerrar}>
            ✕ Cerrar
          </button>
          
          <div className="info-simulador">
            <p>🎮 <strong>Simulador Interactivo</strong></p>
            <p>Selecciona las respuestas para probar el flujo completo</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SimuladorVista;